import { Component, ElementRef, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { PaymentService } from '../services/payment.service';
import { DataService } from '@app/core/services/data.service';
import { dictionaryItems } from '@app/core/constants/dictionaryItems';
import moment from 'moment';
import { RoleService } from '@app/core/services/role.service';
import { PaymentBalanceBreakdownComponent } from '../payment-balance-breakdown/payment-balance-breakdown.component';
import { PaymentSubAccountBreakdownComponent } from '../payment-sub-account-breakdown/payment-sub-account-breakdown.component';
import { SpinnerComponent } from '@app/shared/components/spinner/spinner.component';
import { Subject, Subscription, takeUntil } from 'rxjs';
import { Adb2cService } from '@app/blocks/adb2c/services/adb2c/adb2c.service';

@Component({
    selector: 'app-payment-transaction-history',
    imports: [ CommonModule, PaymentBalanceBreakdownComponent, PaymentSubAccountBreakdownComponent, SpinnerComponent ],
    templateUrl: './payment-transaction-history.component.html',
    styleUrl: './payment-transaction-history.component.scss',
})
export class PaymentTransactionHistoryComponent implements OnInit, OnDestroy {

    errorText: string = "";
    currentFilter: number = 56;
    config: any = {};
    enablePaymentPage: any;
    status = {
        balanceLoaded: false,
        chargesLoaded: false,
        chargesBreakdownLoaded: false,
        transactionsLoaded: false,
        loaded: false
    };
    error = {
        accountError: false,
        transactionsError: false
    };
    user: any;
    subAccount: number = 0;
    transactions: any;
    charges: any = {
        next: "",
        frequency: ""
    };
    balance: any;
    balanceText: string = "";
    isLeaseholder: boolean = false;
    downloadUrl: string = "";

    private destroy$ = new Subject<void>();
    private userSubscription?: Subscription;

    constructor(private paymentService: PaymentService,
        private dataService: DataService,
        private adb2cService: Adb2cService,
        private roleService: RoleService,
        private el: ElementRef) {
    }

    ngOnInit() {
        this.config.filterFromDate = moment().subtract(this.currentFilter, "weeks");
        this.config.filterToDate = moment();
        this.config.enablePaymentPage = this.paymentService.getEnablePaymentPage(); //getting the value from switches
        console.log('Init')

        const cpt = this.el.nativeElement;
        this.errorText = cpt.getAttribute("errorText");

        this.adb2cService.getUser();
        this.dataService.getDictionaryItems()
            .subscribe({
                next: (response) => {
                    this.config.dictionary = response;

                    this.config.balanceText = this.dataService.getDictionaryItemByKeyAndLanguage(this.config.dictionary, dictionaryItems.balance, "en-GB");
                    this.config.detailsText = this.dataService.getDictionaryItemByKeyAndLanguage(this.config.dictionary, dictionaryItems.details, "en-GB");
                    this.config.nextDueText = String(this.dataService.getDictionaryItemByKeyAndLanguage(this.config.dictionary, dictionaryItems.nextDue, "en-GB")).toLowerCase();
                    this.config.makeAPaymentText = this.dataService.getDictionaryItemByKeyAndLanguage(this.config.dictionary, dictionaryItems.makeAPayment, "en-GB");
                    this.config.downloadTransactionsText = this.dataService.getDictionaryItemByKeyAndLanguage(this.config.dictionary, dictionaryItems.downloadTransactions, "en-GB");
                    this.config.currentBalanceText = this.dataService.getDictionaryItemByKeyAndLanguage(this.config.dictionary, dictionaryItems.currentBalance, "en-GB");
                    this.config.inArrearsText = String(this.dataService.getDictionaryItemByKeyAndLanguage(this.config.dictionary, dictionaryItems.inArrears, "en-GB")).toLowerCase();
                    this.config.inCreditText = String(this.dataService.getDictionaryItemByKeyAndLanguage(this.config.dictionary, dictionaryItems.inCredit, "en-GB")).toLowerCase();
                    this.config.toText = this.dataService.getDictionaryItemByKeyAndLanguage(this.config.dictionary, dictionaryItems.to, "en-GB");
                    this.config.fromText = this.dataService.getDictionaryItemByKeyAndLanguage(this.config.dictionary, dictionaryItems.from, "en-GB");
                    this.config.filterText = this.dataService.getDictionaryItemByKeyAndLanguage(this.config.dictionary, dictionaryItems.filter, "en-GB");
                    this.config.amountText = this.dataService.getDictionaryItemByKeyAndLanguage(this.config.dictionary, dictionaryItems.amount, "en-GB");


                    this.userSubscription = this.adb2cService.activeUser.asObservable().pipe(takeUntil(this.destroy$)).subscribe(user => {
                        if (user && user.sub && user[ 'tenancy_reference' ]) {
                            this.user = user;
                            this.subAccount = 0;
                            this.isLeaseholder = this.roleService.isLeaseholder(user);

                            // Transactions
                            this.paymentService.getTransactions(user[ 'tenancy_reference' ]!, this.subAccount, this.config.filterFromDate, this.config.filterToDate)
                                .subscribe({
                                    next: (response) => {
                                        if (response && response.transactions) {
                                            const slicedTransactions = response.transactions.slice(0, this.config.defaultMaxTransactions);
                                            this.processTransactions(slicedTransactions);
                                        }
                                        this.status.transactionsLoaded = true;
                                    },
                                    error: (error) => {
                                        console.error(error);
                                        this.status.transactionsLoaded = true;
                                        this.error.transactionsError = true;
                                    },
                                    complete: () => this.checkAllLoaded() // Check loading state on completion
                                });

                            // Charges
                            this.paymentService.getCharges(user[ 'tenancy_reference' ]!, this.subAccount).subscribe({
                                next: (response) => {
                                    if (response && response.charges) {
                                        const charges = response.charges;
                                        this.processCharges(charges);
                                    }
                                    this.status.chargesLoaded = true;
                                },
                                error: (error) => {
                                    console.error(error);
                                    this.status.chargesLoaded = true;
                                    this.error.accountError = true;
                                },
                                complete: () => this.checkAllLoaded() // Check loading state on completion
                            });

                            // Balance
                            this.paymentService.getBalance(user[ 'tenancy_reference' ]!, this.subAccount).subscribe({
                                next: (response) => {
                                    if (response) {
                                        this.balance = response.body.balance;
                                        this.balanceText = this.paymentService.convertToCurrencyText(this.balance, true);
                                    }
                                    this.status.balanceLoaded = true;
                                },
                                error: (error) => {
                                    console.error(error);
                                    this.status.balanceLoaded = true;
                                    this.error.accountError = true;
                                },
                                complete: () => this.checkAllLoaded() // Check loading state on completion
                            });


                        }
                    });

                },
                error: (error) => {
                    console.log(error);
                }
            });
    }

    checkAllLoaded() {
        if (this.status.transactionsLoaded && this.status.chargesLoaded && this.status.balanceLoaded) {
            this.status.loaded = true; // Update the loading state
        }
    }

    processTransactions(transactions: any[]) {
        if (transactions) {
            transactions.forEach(t => {
                if (t.credit && t.date) {
                    if (t.credit && t.date) {
                        t.balanceText = this.paymentService.convertToCurrencyText(t.balance);
                        t.creditText = this.paymentService.convertToCurrencyText(t.credit);
                        t.date = moment(t.date);
                        t.dateText = this.paymentService.convertDateToText(t.date);
                    }
                }
            })

            this.downloadUrl = "/umbraco/api/transactionhistory/download?tenancyReference=" +
                this.user.tenancy_reference +
                "&subAccount=" +
                this.subAccount +
                "&startDate=" +
                encodeURIComponent(this.paymentService.convertDateToText(this.config.filterFromDate)) +
                "&endDate=" +
                encodeURIComponent(this.paymentService.convertDateToText(this.config.filterToDate));
        }

        return this.transactions = transactions;
    }

    processCharges(charges: any): void {
        this.charges.next = this.paymentService.convertToCurrencyText(charges.next);
        this.charges.date = moment(charges.date);
        this.charges.dateText = this.paymentService.convertDateToText(moment(charges.date));

        this.charges.frequency =
            this.dataService.getDictionaryItemByKeyAndLanguage(this.config.dictionary, dictionaryItems.yourCurrent, "en-GB") +
            " " +
            charges.frequency.toLowerCase() +
            " " +
            this.dataService.getDictionaryItemByKeyAndLanguage(this.config.dictionary, dictionaryItems.rentCharge, "en-GB");

        return;
    }

    filter(value: number) {
        if (value) {
            this.status.transactionsLoaded = false;

            this.currentFilter = value;
            this.config.filterFromDate = moment().subtract(value, "weeks");
            this.config.filterToDate = moment();

            // this.trackFilter(value);
        }

        // Make sure none of the dates are in the future
        if (this.config.filterFromDate.isAfter()) {
            this.config.filterFromDate = moment();
        }
        if (this.config.filterToDate.isAfter()) {
            this.config.filterToDate = moment();
        }

        this.paymentService
            .getTransactions(this.user.tenancy_reference, this.subAccount, this.config.filterFromDate, this.config.filterToDate).subscribe({
                next: (response) => {
                    if (response && response.transactions) {
                        this.processTransactions(response.transactions);
                        this.status.transactionsLoaded = true;
                        this.error.transactionsError = false;
                    }
                },
                error: (error) => {
                    console.log(error);
                    this.transactions = [];
                    this.status.transactionsLoaded = true;
                    this.error.transactionsError = true;
                }
            });
    }

    trackDownload = function () {
        // if (typeof gtag == 'function') {
        //     gtag("event", "download", {
        //         "event_category": "Rent & Charges",
        //         "event_label": window.location.pathname
        //     });
        // }
    }

    // trackFilter = function (value) {
    //     if (typeof gtag == 'function') {

    //         let tag = "filter-" + value + "-weeks";
    //         if (value == 26) {
    //             tag = "filter-6-months";
    //         } else if (value == 56) {
    //             tag = "filter-13-months";
    //         }

    //         gtag("event", tag, {
    //             "event_category": "Rent & Charges",
    //             "event_label": window.location.pathname
    //         });
    //     }
    // }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();

        this.userSubscription?.unsubscribe();
    }
}
